import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { useAppStore } from '../store/appStore';
import { useRegion } from '../hooks/useRegion';
import { getBackendUrl } from '../lib/api';
import { Layout } from '../components/Layout';
import { ROIProjectionCard } from '../components/ROIProjectionCard';
import { ROIMiniCard } from '../components/ROIMiniCard';
import { ROIRingCard } from '../components/ROIRingCard';
import { MVPEquipmentCard, CategoryPerformanceCard, BestKitCard, CategoryBadge, EquipmentPhoneList, EQUIPMENT_SORTS, signedPercent } from '../components/equipment';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { PageHeader } from '../components/PageHeader';
import { Illustration } from '../components/Illustration';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { DatePicker } from '../components/ui/date-picker';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { cn, formatDate, calculateROI, getROIStatus } from '../lib/utils';
import { OnboardingTrack } from '../components/support/OnboardingTrack';
import { supportApi } from '../lib/support';
import { Plus, Package, TrendingUp, TrendingDown, Search, Loader2, Trash2, Edit, RefreshCw, Archive, RotateCcw, FileDown, Boxes, Upload, Download, Database, MoreHorizontal, FileSpreadsheet, FileUp, Headset } from 'lucide-react';
import { toast } from 'sonner';
import { XeroImporter } from '../components/XeroImporter';
import { PhonePanel } from '../components/PhonePanel';
import { useIsPhone } from '../hooks/useIsPhone';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { PageToolbar, TOOLBAR_HEIGHT, SECONDARY_TOOLBAR_HEIGHT, STICKY_TABLE, stickyTableHeader } from '../components/PageToolbar';
import FileDropzone from '@/components/FileDropzone';

const CATEGORIES = [
  'Camera',
  'Lenses',
  'Lighting',
  'Grip',
  'Audio',
  'Monitors',
  'Accessories',
  'Other'
];

const INITIAL_FORM = {
  name: '',
  description: '',
  category: 'Camera',
  purchase_price: '',
  purchase_date: '',
  daily_rate: '',
  weekly_rate: '',
  is_active: true
};

const INITIAL_KIT_FORM = {
  name: '',
  equipment_items: []
};

// The bulk-delete bar: 60px, plus 12px below it before the table header
const BULK_BAR_HEIGHT = 72;

// Equipment tables: headings and figures never wrap, number columns get even
// room, and the first and last columns line up with the 24px panel padding
// above. The Name column takes whatever width is left; below 1200px the
// purchase price and rental days drop out so it keeps room to read.
const EQUIPMENT_TABLE_CLASS =
  '[&_th]:whitespace-nowrap [&_td.text-right]:whitespace-nowrap [&_th]:px-5 [&_td]:px-5 ' +
  '[&_tr>*:first-child]:pl-6 [&_tr>*:last-child]:pr-6';

export default function EquipmentPage() {
  // Whether to offer the onboarding service on an empty inventory, or show
  // how far along it is once bought
  const subscription = useAuthStore((st) => st.subscription);
  const isDemo = useAuthStore((st) => st.isDemo);
  const [onboarding, setOnboarding] = useState(null);
  useEffect(() => {
    if (!subscription?.onboarding_purchased || isDemo) return;
    supportApi.myOnboarding().then(setOnboarding).catch(() => {});
  }, [subscription?.onboarding_purchased, isDemo]);
  const navigate = useNavigate();
  const { 
    equipment, fetchEquipment, createEquipment, updateEquipment, deleteEquipment, 
    restoreEquipment, permanentDeleteEquipment, recalculateEquipmentROI, 
    downloadEquipmentListPDF, equipmentLoading,
    equipmentKits, fetchEquipmentKits, createEquipmentKit, updateEquipmentKit, 
    deleteEquipmentKit, kitsLoading
  } = useAppStore();
  const { formatCurrency, regionConfig } = useRegion();
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const isPhone = useIsPhone();
  const showInsightsGrid = useMediaQuery('(min-width: 768px)');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [recalculating, setRecalculating] = useState(false);
  const [activeTab, setActiveTab] = useState('active'); // 'active', 'kits', or 'archived'
  const [restoring, setRestoring] = useState(null);
  const [deleting, setDeleting] = useState(null);
  
  // Kit state
  const [isKitDialogOpen, setIsKitDialogOpen] = useState(false);
  const [editingKitId, setEditingKitId] = useState(null);
  const [kitFormData, setKitFormData] = useState(INITIAL_KIT_FORM);
  const [selectedProjectionKitId, setSelectedProjectionKitId] = useState(null);
  
  // Import CSV state
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState([]);
  
  // Full Export/Import (with history) state
  const [isFullImportDialogOpen, setIsFullImportDialogOpen] = useState(false);
  const [fullImporting, setFullImporting] = useState(false);
  const [fullImportFile, setFullImportFile] = useState(null);
  const [fullImportPreview, setFullImportPreview] = useState([]);
  const [fullExporting, setFullExporting] = useState(false);
  
  // Bulk delete state
  const [bulkDeleteMode, setBulkDeleteMode] = useState(false);
  const [selectedForDelete, setSelectedForDelete] = useState(new Set());
  
  // Disposal state
  const [showDisposalDialog, setShowDisposalDialog] = useState(false);
  const [disposingEquipmentId, setDisposingEquipmentId] = useState(null);
  const [disposalForm, setDisposalForm] = useState({
    sale_price: '',
    disposal_date: new Date().toISOString().split('T')[0],
    buyer_notes: ''
  });
  const [bulkDeleting, setBulkDeleting] = useState(false);
  
  // Xero Importer state
  const [isXeroImporterOpen, setIsXeroImporterOpen] = useState(false);

  useEffect(() => {
    fetchEquipment();
    fetchEquipmentKits();
  }, [fetchEquipment, fetchEquipmentKits]);

  // Handle edit query parameter from search navigation
  useEffect(() => {
    const editId = searchParams.get('edit');
    if (editId && equipment.length > 0) {
      const itemToEdit = equipment.find(e => e.id === editId);
      if (itemToEdit) {
        setFormData({
          name: itemToEdit.name || '',
          category: itemToEdit.category || 'Camera',
          purchase_price: itemToEdit.purchase_price?.toString() || '',
          purchase_date: itemToEdit.purchase_date || '',
          day_rate: itemToEdit.day_rate?.toString() || '',
          notes: itemToEdit.notes || '',
          serial_number: itemToEdit.serial_number || '',
        });
        setEditingId(itemToEdit.id);
        setIsDialogOpen(true);
        // Clear the query param after opening
        setSearchParams({});
      }
    }
  }, [searchParams, equipment, setSearchParams]);

  const handleRecalculateROI = async () => {
    setRecalculating(true);
    try {
      const result = await recalculateEquipmentROI();
      toast.success(result.message || 'ROI recalculated');
    } catch (error) {
      toast.error('Failed to recalculate ROI');
    } finally {
      setRecalculating(false);
    }
  };

  // Bulk delete all historical income entries
  const handleBulkDeleteHistoricalIncome = async () => {
    // First confirmation
    const firstConfirm = window.confirm(
      'Are you sure you want to delete ALL historical income entries from ALL equipment?\n\n' +
      'This will:\n' +
      '• Remove all manually imported historical income\n' +
      '• Preserve income from completed jobs\n' +
      '• Recalculate ROI for all affected equipment\n\n' +
      'This action cannot be undone.'
    );
    
    if (!firstConfirm) return;
    
    // Second confirmation - type DELETE
    const typedConfirm = window.prompt(
      'This is a destructive action!\n\n' +
      'Type "DELETE" to confirm:'
    );
    
    if (typedConfirm !== 'DELETE') {
      toast.error('Deletion cancelled - you must type DELETE to confirm');
      return;
    }
    
    const toastId = toast.loading('Deleting historical income entries...');
    try {
      const response = await fetch(`${getBackendUrl()}/api/equipment/historical-income/bulk-delete`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('session_token')}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete historical income');
      }
      
      const result = await response.json();
      toast.success(result.message, { id: toastId });
      
      // Refresh equipment list
      await fetchEquipment();
    } catch (error) {
      console.error('Bulk delete error:', error);
      toast.error('Failed to delete historical income', { id: toastId });
    }
  };

  const handleDownloadPDF = async () => {
    const toastId = toast.loading('Generating Equipment List PDF...');
    try {
      await downloadEquipmentListPDF();
      toast.success('Equipment List PDF downloaded!', { id: toastId });
    } catch (error) {
      console.error('PDF download error:', error);
      toast.error('Failed to generate PDF', { id: toastId });
    }
  };

  // Download Equipment as CSV
  const handleDownloadCSV = () => {
    // Filter active equipment only
    const activeEquipment = equipment.filter(e => e.is_active !== false);
    
    if (activeEquipment.length === 0) {
      toast.error('No equipment to export');
      return;
    }
    
    // Use same format as import template
    const headers = ['name', 'category', 'description', 'purchase_price', 'daily_rate', 'purchase_date'];
    
    const rows = activeEquipment.map(item => [
      item.name || '',
      item.category || '',
      item.description || '',
      item.purchase_price?.toString() || '',
      item.daily_rate?.toString() || '',
      item.purchase_date ? item.purchase_date.split('T')[0] : ''
    ]);
    
    // Build CSV with proper escaping
    const csvContent = [headers, ...rows].map(row => 
      row.map(cell => {
        const cellStr = String(cell);
        if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
          return `"${cellStr.replace(/"/g, '""')}"`;
        }
        return cellStr;
      }).join(',')
    ).join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `equipment_export_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    
    toast.success(`Exported ${activeEquipment.length} items to CSV`);
  };

  // Download CSV Template
  const handleDownloadTemplate = () => {
    // Proper CSV with headers and example rows (no comment lines)
    const rows = [
      ['name', 'category', 'description', 'purchase_price', 'daily_rate', 'purchase_date'],
      ['RED Komodo 6K Camera', 'Camera', 'Includes body and top handle', '5995.00', '350.00', '2024-01-15'],
      ['Canon 24-70mm f/2.8L', 'Lenses', 'Professional zoom lens', '1899.00', '75.00', '2024-02-20'],
      ['Aputure 600d Pro', 'Lighting', 'LED light with bowens mount', '1290.00', '125.00', '2024-03-10'],
    ];
    
    // Convert to CSV with proper escaping
    const csvContent = rows.map(row => 
      row.map(cell => {
        // Escape cells that contain commas or quotes
        if (cell.includes(',') || cell.includes('"') || cell.includes('\n')) {
          return `"${cell.replace(/"/g, '""')}"`;
        }
        return cell;
      }).join(',')
    ).join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'equipment_import_template.csv');
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  // Parse CSV line handling quoted fields
  const parseCSVLine = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      
      if (char === '"') {
        if (inQuotes && line[i + 1] === '"') {
          current += '"';
          i++; // Skip next quote
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    result.push(current.trim());
    return result;
  };

  // Parse entire CSV text handling quoted fields with embedded newlines
  const parseCSVText = (text) => {
    const rows = [];
    let currentRow = [];
    let currentField = '';
    let inQuotes = false;
    
    for (let i = 0; i < text.length; i++) {
      const char = text[i];
      const nextChar = text[i + 1];
      
      if (char === '"') {
        if (inQuotes && nextChar === '"') {
          // Escaped quote inside quoted field
          currentField += '"';
          i++; // Skip next quote
        } else {
          // Toggle quote state
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        // End of field
        currentRow.push(currentField.trim());
        currentField = '';
      } else if ((char === '\n' || (char === '\r' && nextChar === '\n')) && !inQuotes) {
        // End of row (handle both \n and \r\n)
        currentRow.push(currentField.trim());
        if (currentRow.some(field => field !== '')) {
          rows.push(currentRow);
        }
        currentRow = [];
        currentField = '';
        if (char === '\r') i++; // Skip \n in \r\n
      } else if (char === '\r' && !inQuotes) {
        // Handle standalone \r as line ending
        currentRow.push(currentField.trim());
        if (currentRow.some(field => field !== '')) {
          rows.push(currentRow);
        }
        currentRow = [];
        currentField = '';
      } else {
        // Regular character (including newlines inside quotes)
        currentField += char;
      }
    }
    
    // Don't forget the last field and row
    currentRow.push(currentField.trim());
    if (currentRow.some(field => field !== '')) {
      rows.push(currentRow);
    }
    
    return rows;
  };

  // Handle CSV file selection
  const handleFileSelect = (file) => {
    if (!file) return;
    
    if (!file.name.endsWith('.csv')) {
      toast.error('Please select a CSV file');
      return;
    }
    
    setImportFile(file);
    
    // Preview the CSV
    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      // Use parseCSVText to properly handle quoted fields with embedded newlines
      const csvRows = parseCSVText(text);
      
      if (csvRows.length < 2) {
        toast.error('CSV file must have headers and at least one data row');
        setImportFile(null);
        return;
      }
      
      const headers = csvRows[0].map(h => h.toLowerCase());
      // Require 'name' column
      const requiredHeaders = ['name', 'category', 'daily_rate'];
      const missingHeaders = requiredHeaders.filter(h => !headers.includes(h));
      
      if (missingHeaders.length > 0) {
        toast.error(`Missing required columns: ${missingHeaders.join(', ')}`);
        setImportFile(null);
        return;
      }
      
      const preview = [];
      for (let i = 1; i < Math.min(csvRows.length, 6); i++) {
        const values = csvRows[i];
        const item = {};
        headers.forEach((header, idx) => {
          item[header] = values[idx] || '';
        });
        preview.push(item);
      }
      
      setImportPreview(preview);
    };
    reader.readAsText(file);
  };

  // Handle CSV import
  const handleImportCSV = async () => {
    if (!importFile) return;
    
    setImporting(true);
    const toastId = toast.loading('Importing equipment...');
    
    try {
      const reader = new FileReader();
      reader.onload = async (event) => {
        const text = event.target.result;
        // Use parseCSVText to properly handle quoted fields with embedded newlines
        const csvRows = parseCSVText(text);
        
        if (csvRows.length < 2) {
          toast.error('CSV file must have headers and at least one data row', { id: toastId });
          setImporting(false);
          return;
        }
        
        const headers = csvRows[0].map(h => h.toLowerCase());
        
        let imported = 0;
        let errors = 0;
        
        for (let i = 1; i < csvRows.length; i++) {
          const values = csvRows[i];
          const item = {};
          headers.forEach((header, idx) => {
            item[header] = values[idx] || '';
          });
          
          // Skip empty rows
          if (!item.name) continue;
          
          // Validate category
          const validCategories = ['Camera', 'Lenses', 'Lighting', 'Grip', 'Audio', 'Monitors', 'Accessories', 'Other'];
          const category = validCategories.find(c => c.toLowerCase() === item.category?.toLowerCase()) || 'Other';
          
          try {
            await createEquipment({
              name: item.name,
              category: category,
              description: item.description || '',
              purchase_price: parseFloat(item.purchase_price) || 0,
              daily_rate: parseFloat(item.daily_rate) || 0,
              purchase_date: item.purchase_date || null,
              is_active: true
            });
            imported++;
          } catch (err) {
            console.error('Failed to import item:', item.name, err);
            errors++;
          }
        }
        
        setImporting(false);
        setIsImportDialogOpen(false);
        setImportFile(null);
        setImportPreview([]);
        
        if (imported > 0) {
          toast.success(`Imported ${imported} equipment item${imported > 1 ? 's' : ''}${errors > 0 ? ` (${errors} failed)` : ''}`, { id: toastId });
          fetchEquipment();
        } else {
          toast.error('No items were imported', { id: toastId });
        }
      };
      reader.readAsText(importFile);
    } catch (error) {
      console.error('Import error:', error);
      toast.error('Failed to import CSV', { id: toastId });
      setImporting(false);
    }
  };

  // ============== FULL EXPORT/IMPORT (WITH HISTORY) ==============
  
  // Handle Full Export (Equipment + History)
  const handleFullExport = async () => {
    setFullExporting(true);
    const toastId = toast.loading('Exporting equipment with history...');
    
    try {
      const token = localStorage.getItem('session_token');
      const API_URL = getBackendUrl();
      const response = await fetch(`${API_URL}/api/equipment/export-full-csv`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'text/csv'
        }
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Export failed');
      }
      
      // Verify we got CSV content, not HTML (SPA fallback)
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('text/html')) {
        throw new Error('Export endpoint not available. Please try again.');
      }
      
      const blob = await response.blob();
      
      // Additional check: verify blob content starts with CSV headers
      const textCheck = await blob.slice(0, 100).text();
      if (textCheck.includes('<!doctype') || textCheck.includes('<html')) {
        throw new Error('Export failed - received HTML instead of CSV. Please refresh and try again.');
      }
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `equipment_full_export_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Full export downloaded!', { id: toastId });
    } catch (error) {
      console.error('Full export error:', error);
      toast.error(error.message || 'Failed to export', { id: toastId });
    } finally {
      setFullExporting(false);
    }
  };
  
  // Download Full CSV Template
  const handleDownloadFullTemplate = () => {
    const rows = [
      ['name', 'category', 'description', 'purchase_price', 'daily_rate', 'purchase_date', 'history_date', 'history_description', 'history_client', 'history_days', 'history_income', 'history_type'],
      ['RED Komodo 6K Camera', 'Camera', 'Main camera body', '5995.00', '350.00', '2024-01-15', '2024-02-15', 'Corporate shoot', 'Acme Corp', '2', '700.00', 'historical'],
      ['', '', '', '', '', '', '2024-03-20', 'Music video production', 'Sony Music', '3', '1050.00', 'historical'],
      ['', '', '', '', '', '', '2024-04-10', 'Documentary', 'National Geographic', '5', '1750.00', 'historical'],
      ['Canon 24-70mm f/2.8L', 'Lenses', 'Professional zoom lens', '1899.00', '75.00', '2024-02-20', '', '', '', '', '', ''],
    ];
    
    const csvContent = rows.map(row => 
      row.map(cell => {
        if (cell.includes(',') || cell.includes('"') || cell.includes('\n')) {
          return `"${cell.replace(/"/g, '""')}"`;
        }
        return cell;
      }).join(',')
    ).join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'equipment_full_import_template.csv');
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };
  
  // Handle Full CSV file selection
  const handleFullFileSelect = (file) => {
    if (!file) return;
    
    if (!file.name.endsWith('.csv')) {
      toast.error('Please select a CSV file');
      return;
    }
    
    setFullImportFile(file);
    
    // Preview the CSV
    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      // Use parseCSVText to properly handle quoted fields with embedded newlines
      const csvRows = parseCSVText(text);
      
      if (csvRows.length < 2) {
        toast.error('CSV file must have headers and at least one data row');
        setFullImportFile(null);
        return;
      }
      
      const headers = csvRows[0].map(h => h.toLowerCase().replace(/\s+/g, '_'));
      
      // Check for required header (accept both 'name' and 'equipment_name')
      if (!headers.includes('name') && !headers.includes('equipment_name')) {
        toast.error('Missing required column: name or equipment_name');
        setFullImportFile(null);
        return;
      }
      
      // Parse and preview
      const preview = { equipment: [], historyCount: 0 };
      let currentEquipment = null;
      
      for (let i = 1; i < csvRows.length; i++) {
        const values = csvRows[i];
        const row = {};
        headers.forEach((header, idx) => {
          row[header] = values[idx] || '';
        });
        
        const equipName = row.name || row.equipment_name;
        if (equipName && equipName.trim()) {
          if (currentEquipment) {
            preview.equipment.push(currentEquipment);
          }
          currentEquipment = {
            name: equipName,
            category: row.category || 'Other',
            daily_rate: row.daily_rate || '0',
            historyEntries: row.history_date ? 1 : 0
          };
        } else if (currentEquipment && row.history_date) {
          currentEquipment.historyEntries++;
          preview.historyCount++;
        }
      }
      if (currentEquipment) {
        preview.equipment.push(currentEquipment);
      }
      
      setFullImportPreview(preview);
    };
    reader.readAsText(file);
  };
  
  // Handle Full CSV import
  const handleFullImportCSV = async () => {
    if (!fullImportFile) return;
    
    setFullImporting(true);
    const toastId = toast.loading('Importing equipment with history...');
    
    try {
      const reader = new FileReader();
      reader.onload = async (event) => {
        try {
          const text = event.target.result;
          // Use parseCSVText to properly handle quoted fields with embedded newlines
          const csvRows = parseCSVText(text);
          
          if (csvRows.length < 2) {
            throw new Error('CSV file must have headers and at least one data row');
          }
          
          const headers = csvRows[0].map(h => h.toLowerCase().replace(/\s+/g, '_'));
          
          // Build rows array for API
          const rows = [];
          for (let i = 1; i < csvRows.length; i++) {
            const values = csvRows[i];
            const row = {};
            headers.forEach((header, idx) => {
              row[header] = values[idx] || '';
            });
            
            rows.push({
              name: row.name || row.equipment_name || null,
              category: row.category || null,
              description: row.description || null,
              purchase_price: parseFloat(row.purchase_price) || 0,
              daily_rate: parseFloat(row.daily_rate) || 0,
              purchase_date: row.purchase_date || null,
              history_date: row.history_date || null,
              history_description: row.history_description || null,
              history_client: row.history_client || null,
              history_days: parseFloat(row.history_days) || 0,
              history_income: parseFloat(row.history_income) || 0,
              history_type: row.history_type || 'historical'
            });
          }
          
          const token = localStorage.getItem('session_token');
          const API_URL = getBackendUrl();
          const response = await fetch(`${API_URL}/api/equipment/import-full-csv`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ rows })
          });
          
          if (!response.ok) {
            const error = await response.json();
            throw new Error(error.detail || 'Import failed');
          }
          
          const result = await response.json();
          
          setFullImporting(false);
          setIsFullImportDialogOpen(false);
          setFullImportFile(null);
          setFullImportPreview([]);
          
          toast.success(result.message, { id: toastId });
          fetchEquipment();
        } catch (error) {
          console.error('Full import error:', error);
          toast.error(error.message || 'Failed to import', { id: toastId });
          setFullImporting(false);
        }
      };
      reader.readAsText(fullImportFile);
    } catch (error) {
      console.error('Full import error:', error);
      toast.error('Failed to import CSV', { id: toastId });
      setFullImporting(false);
    }
  };

  // ============== BULK DELETE FUNCTIONS ==============
  
  const toggleBulkDeleteMode = () => {
    if (bulkDeleteMode) {
      // Exiting bulk delete mode
      setBulkDeleteMode(false);
      setSelectedForDelete(new Set());
    } else {
      // Entering bulk delete mode
      setBulkDeleteMode(true);
      setSelectedForDelete(new Set());
    }
  };
  
  const toggleSelectItem = (itemId, e) => {
    e.stopPropagation(); // Prevent row click navigation
    setSelectedForDelete(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };
  
  const selectAllItems = () => {
    const activeItems = filteredEquipment.filter(e => e.is_active !== false);
    if (selectedForDelete.size === activeItems.length) {
      // All selected, deselect all
      setSelectedForDelete(new Set());
    } else {
      // Select all
      setSelectedForDelete(new Set(activeItems.map(e => e.id)));
    }
  };
  
  const handleBulkDelete = async () => {
    if (selectedForDelete.size === 0) return;
    
    // Confirmation dialog for permanent deletion
    const itemCount = selectedForDelete.size;
    const confirmed = window.confirm(
      `Permanently delete ${itemCount} item${itemCount > 1 ? 's' : ''}?\n\nThis will permanently delete the selected equipment and ALL their income history. This cannot be undone.`
    );
    
    if (!confirmed) return;
    
    setBulkDeleting(true);
    const toastId = toast.loading(`Deleting ${selectedForDelete.size} items...`);
    
    let successCount = 0;
    let errorCount = 0;
    
    for (const itemId of selectedForDelete) {
      try {
        // Directly permanently delete the item
        await permanentDeleteEquipment(itemId);
        successCount++;
      } catch (error) {
        console.error(`Failed to delete item ${itemId}:`, error);
        errorCount++;
      }
    }
    
    setBulkDeleting(false);
    setBulkDeleteMode(false);
    setSelectedForDelete(new Set());
    
    if (errorCount === 0) {
      toast.success(`Permanently deleted ${successCount} items`, { id: toastId });
    } else {
      toast.warning(`Deleted ${successCount} items, ${errorCount} failed`, { id: toastId });
    }
    
    fetchEquipment();
  };

  const filteredEquipment = equipment
    .filter((item) => {
      // Filter by active/archived status based on current tab
      const isActive = item.is_active !== false; // Default to active if not specified
      if (activeTab === 'active' && !isActive) return false;
      if (activeTab === 'archived' && isActive) return false;
      
      const matchesSearch = item.name?.toLowerCase().includes(search.toLowerCase());
      const matchesCategory = categoryFilter === 'all' || item.category === categoryFilter;
      return matchesSearch && matchesCategory;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'roi':
          return (b.calculated_roi || 0) - (a.calculated_roi || 0);
        case 'income':
          return (b.total_income || 0) - (a.total_income || 0);
        case 'days':
          return (b.total_days || 0) - (a.total_days || 0);
        default:
          return a.name.localeCompare(b.name);
      }
    });

  // Each row's ROI, worked out once for the table and the phone list.
  // An archived item's sale price counts toward what it earned.
  const activeRows = filteredEquipment.filter(e => e.is_active !== false).map((item) => {
    const roi = calculateROI(item.total_income || 0, item.purchase_price || 0);
    return { item, roi, roiStatus: getROIStatus(roi) };
  });
  const archivedRows = filteredEquipment.filter(e => e.is_active === false).map((item) => {
    const roi = calculateROI(item.total_income || 0, item.purchase_price || 0, item.sale_price || 0);
    return { item, roi, roiStatus: getROIStatus(roi) };
  });

  // Count active and archived items
  const activeCount = equipment.filter(e => e.is_active !== false).length;
  const archivedCount = equipment.filter(e => e.is_active === false).length;
  
  // Active equipment for kit selection (only non-archived items)
  const activeEquipment = equipment.filter(e => e.is_active !== false);

  const openCreateDialog = () => {
    setEditingId(null);
    setFormData(INITIAL_FORM);
    setIsDialogOpen(true);
  };

  const openEditDialog = (item) => {
    setEditingId(item.id);
    setFormData({
      name: item.name || '',
      description: item.description || '',
      category: item.category || 'Camera',
      purchase_price: item.purchase_price?.toString() || '',
      purchase_date: item.purchase_date || '',
      daily_rate: item.daily_rate?.toString() || '',
      weekly_rate: item.weekly_rate?.toString() || '',
      is_active: item.is_active ?? true
    });
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        purchase_price: parseFloat(formData.purchase_price) || 0,
        daily_rate: parseFloat(formData.daily_rate) || 0,
        weekly_rate: formData.weekly_rate !== '' ? parseFloat(formData.weekly_rate) : null,
      };

      if (editingId) {
        await updateEquipment(editingId, data);
        toast.success('Equipment updated successfully');
      } else {
        await createEquipment(data);
        toast.success('Equipment added successfully');
      }
      
      setIsDialogOpen(false);
      setEditingId(null);
      setFormData(INITIAL_FORM);
    } catch (error) {
      toast.error(editingId ? 'Failed to update equipment' : 'Failed to add equipment');
    }
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation(); // Prevent row click
    setDisposingEquipmentId(id);
    setShowDisposalDialog(true);
  };
  
  const handleDisposalSubmit = async () => {
    try {
      const disposal = {
        sale_price: disposalForm.sale_price ? parseFloat(disposalForm.sale_price) : null,
        disposal_date: disposalForm.disposal_date || null,
        buyer_notes: disposalForm.buyer_notes || null
      };
      
      // Call deleteEquipment with disposal data
      await deleteEquipment(disposingEquipmentId, disposal);
      toast.success('Equipment archived successfully');
      
      // Reset form
      setShowDisposalDialog(false);
      setDisposingEquipmentId(null);
      setDisposalForm({
        sale_price: '',
        disposal_date: new Date().toISOString().split('T')[0],
        buyer_notes: ''
      });
    } catch (error) {
      toast.error('Failed to archive equipment');
    }
  };
  
  const handleDisposalCancel = () => {
    setShowDisposalDialog(false);
    setDisposingEquipmentId(null);
    setDisposalForm({
      sale_price: '',
      disposal_date: new Date().toISOString().split('T')[0],
      buyer_notes: ''
    });
  };

  const handleRestore = async (id, e) => {
    e.stopPropagation(); // Prevent row click
    setRestoring(id);
    try {
      await restoreEquipment(id);
      toast.success('Equipment restored successfully');
    } catch (error) {
      toast.error('Failed to restore equipment');
    } finally {
      setRestoring(null);
    }
  };

  const handlePermanentDelete = async (id, name, e) => {
    e.stopPropagation(); // Prevent row click
    if (window.confirm(`Permanently delete "${name}"?\n\nThis will permanently delete this equipment and ALL its income history. This cannot be undone.`)) {
      setDeleting(id);
      try {
        await permanentDeleteEquipment(id);
        toast.success('Equipment permanently deleted');
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to delete equipment');
      } finally {
        setDeleting(null);
      }
    }
  };

  // Kit handlers
  const handleKitSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingKitId) {
        await updateEquipmentKit(editingKitId, kitFormData);
        toast.success('Kit updated successfully');
      } else {
        await createEquipmentKit(kitFormData);
        toast.success('Kit created successfully');
      }
      setIsKitDialogOpen(false);
      setEditingKitId(null);
      setKitFormData(INITIAL_KIT_FORM);
    } catch (error) {
      toast.error(editingKitId ? 'Failed to update kit' : 'Failed to create kit');
    }
  };

  const handleEditKit = (kit) => {
    setEditingKitId(kit.id);
    setKitFormData({
      name: kit.name,
      equipment_items: kit.equipment_items || []
    });
    setIsKitDialogOpen(true);
  };

  const handleDeleteKit = async (kitId, e) => {
    e.stopPropagation();
    if (window.confirm('Are you sure you want to delete this kit?')) {
      try {
        await deleteEquipmentKit(kitId);
        toast.success('Kit deleted successfully');
      } catch (error) {
        toast.error('Failed to delete kit');
      }
    }
  };

  const toggleEquipmentInKit = (equipId) => {
    const existingIndex = kitFormData.equipment_items.findIndex(
      item => item.equipment_id === equipId
    );
    
    if (existingIndex >= 0) {
      // Remove from kit
      setKitFormData({
        ...kitFormData,
        equipment_items: kitFormData.equipment_items.filter(item => item.equipment_id !== equipId)
      });
    } else {
      // Add to kit - just store equipment_id
      setKitFormData({
        ...kitFormData,
        equipment_items: [...kitFormData.equipment_items, { equipment_id: equipId }]
      });
    }
  };

  const getKitEquipmentNames = (kit) => {
    if (!kit.equipment_items || kit.equipment_items.length === 0) return 'No items';
    return kit.equipment_items.map(item => {
      const equip = equipment.find(e => e.id === item.equipment_id);
      if (!equip) return 'Unknown';
      // Show archived status if archived
      return equip.archived ? `${equip.name} (Archived)` : equip.name;
    }).join(', ');
  };

  const totalValue = equipment.reduce((sum, e) => sum + (e.is_active ? (e.purchase_price || 0) : 0), 0);
  const totalIncome = equipment.reduce((sum, e) => sum + (e.total_income || 0), 0);
  const totalROI = totalValue > 0 ? ((totalIncome - totalValue) / totalValue) * 100 : 0;

  return (
    <Layout>
      <div className="space-y-8 animate-in" data-testid="equipment-page">
        <PageHeader title="Equipment" />

        {/* Equipment Dialog */}
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingId ? 'Edit equipment' : 'Add equipment'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., ARRI Alexa Mini"
                  required
                  data-testid="equipment-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="category">Category</Label>
                <Select
                  value={formData.category}
                  onValueChange={(value) => setFormData({ ...formData, category: value })}
                >
                  <SelectTrigger data-testid="equipment-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((cat) => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Description (package contents)</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="e.g., Includes: camera body, 2x V-mount batteries, charger, top handle, monitor mount..."
                  className="h-20"
                  data-testid="equipment-description-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="purchase_price">Purchase price ({regionConfig.currency.symbol})</Label>
                  <Input
                    id="purchase_price"
                    type="number"
                    step="0.01"
                    value={formData.purchase_price}
                    onChange={(e) => setFormData({ ...formData, purchase_price: e.target.value })}
                    placeholder="0.00"
                    data-testid="equipment-price-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="daily_rate">Daily rate ({regionConfig.currency.symbol})</Label>
                  <Input
                    id="daily_rate"
                    type="number"
                    step="0.01"
                    value={formData.daily_rate}
                    onChange={(e) => setFormData({ ...formData, daily_rate: e.target.value })}
                    placeholder="0.00"
                    data-testid="equipment-rate-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="weekly_rate">Weekly rate ({regionConfig.currency.symbol}) <span className="text-muted-foreground font-normal text-xs">optional</span></Label>
                  <Input
                    id="weekly_rate"
                    type="number"
                    step="0.01"
                    value={formData.weekly_rate}
                    onChange={(e) => setFormData({ ...formData, weekly_rate: e.target.value })}
                    placeholder="0.00"
                    data-testid="equipment-weekly-rate-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="purchase_date">Purchase date</Label>
                <DatePicker
                  value={formData.purchase_date}
                  onChange={(value) => setFormData({ ...formData, purchase_date: value })}
                  placeholder="Select date"
                  data-testid="equipment-date-input"
                  region={regionConfig.code}
                />
              </div>
              <Button type="submit" className="w-full" data-testid="save-equipment-btn">
                {editingId ? 'Save changes' : 'Add equipment'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Main Tab Navigation - Active / Kits / Archived */}
        {isPhone ? (
          <PhonePanel
            tabs={{
              value: activeTab,
              onChange: setActiveTab,
              items: [
                { value: 'active', label: 'Active', count: activeCount },
                { value: 'kits', label: 'Kits', count: equipmentKits.length },
                { value: 'archived', label: 'Archived', count: archivedCount },
              ],
            }}
            actions={activeTab === 'archived' ? [] : [
              { label: 'Create kit', onClick: () => { setEditingKitId(null); setKitFormData(INITIAL_KIT_FORM); setIsKitDialogOpen(true); }, testId: 'create-kit-btn' },
              { label: 'Add equipment', icon: Plus, onClick: openCreateDialog, primary: true, testId: 'add-equipment-btn' },
            ]}
          />
        ) : (
        <PageToolbar className="sm:flex-row sm:items-center sm:justify-between" hairline={false}>
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full sm:w-auto">
            <TabsList>
              <TabsTrigger value="active" className="flex items-center gap-2">
                <Package className="w-4 h-4 hidden sm:block" />
                Active <span className="opacity-75 text-sm">{activeCount}</span>
              </TabsTrigger>
              <TabsTrigger value="kits" className="flex items-center gap-2">
                <Boxes className="w-4 h-4 hidden sm:block" />
                Kits <span className="opacity-75 text-sm">{equipmentKits.length}</span>
              </TabsTrigger>
              <TabsTrigger value="archived" className="flex items-center gap-2">
                <Archive className="w-4 h-4 hidden sm:block" />
                Archived <span className="opacity-75 text-sm">{archivedCount}</span>
              </TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="flex items-center gap-2">
            {(activeTab === 'kits' || activeTab === 'active') && (
              <>
                <Button onClick={() => { setEditingKitId(null); setKitFormData(INITIAL_KIT_FORM); setIsKitDialogOpen(true); }} variant="secondary" data-testid="create-kit-btn">
                  <Boxes className="w-[18px] h-[18px]" /> Create kit
                </Button>
                <Button onClick={openCreateDialog} data-testid="add-equipment-btn">
                  <Plus className="w-[18px] h-[18px]" /> Add equipment
                </Button>
              </>
            )}
          </div>
        </PageToolbar>
        )}

        {/* Active Equipment Tab Content */}
        {activeTab === 'active' && (
          <>
            {/* Insights, if there's equipment. From md the bento grid; below it
                just income over time, with a pointer to the rest. One or the
                other is rendered, so a phone doesn't load the grid's cards */}
            {equipment.filter(e => e.is_active !== false).length > 0 && !showInsightsGrid && (
              <div className="space-y-2" data-testid="equipment-income-phone">
                <div className="h-[220px]">
                  <ROIMiniCard disableLink={true} />
                </div>
                <p className="text-sm text-muted-foreground text-center">More insights available on desktop</p>
              </div>
            )}
            {equipment.filter(e => e.is_active !== false).length > 0 && showInsightsGrid && (
              <div className="hidden md:grid md:grid-cols-6 gap-4">
                {/* 
                  Layout (6 columns):
                  Row 1 (220px): | ROI Overview (4 cols) | ROI Ring (2 cols) |
                  Row 2 (180px): | MVP (2) | Runner Up (2) | Best Kit (2) |
                  Row 3 (160px): | Category Performance (6 cols - full width) |
                */}
                
                {/* ROI Overview - Columns 1-4 */}
                <div className="col-span-4 min-h-[272px] grid [&>*]:h-auto">
                  <ROIMiniCard disableLink={true} />
                </div>
                
                {/* ROI Ring - Columns 5-6 */}
                <div className="col-span-2 min-h-[272px] grid [&>*]:h-auto">
                  <ROIRingCard disableLink={true} />
                </div>
                
                {/* MVP - Columns 1-2 (row 2) */}
                <div className="col-span-2 min-h-[272px] grid [&>*]:h-auto">
                  <MVPEquipmentCard variant="mvp" />
                </div>
                
                {/* Runner Up - Columns 3-4 (row 2) */}
                <div className="col-span-2 min-h-[272px] grid [&>*]:h-auto">
                  <MVPEquipmentCard variant="runner-up" />
                </div>
                
                {/* Best Kit - Columns 5-6 (row 2) */}
                <div className="col-span-2 min-h-[272px] grid [&>*]:h-auto">
                  <BestKitCard onCreateKit={() => { setEditingKitId(null); setKitFormData(INITIAL_KIT_FORM); setIsKitDialogOpen(true); }} />
                </div>
                
                {/* Category Performance - Full width row 3 */}
                <div className="col-span-6">
                  <CategoryPerformanceCard />
                </div>
              </div>
            )}
            
            {/* Search and filters: stick under the page toolbar while the list
                scrolls. A phone has them in its PhonePanel instead */}
            {!isPhone && (
            <PageToolbar secondary hairline={false} className="sm:flex-row sm:items-center lg:justify-start lg:flex-nowrap">
              <div className="relative w-full sm:w-auto sm:flex-[2_1_240px] sm:min-w-[160px]">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-[18px] h-[18px] text-muted-foreground pointer-events-none" />
                <Input
                  placeholder="Search equipment"
                  aria-label="Search equipment"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-11"
                  data-testid="equipment-search-input"
                />
              </div>
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-full sm:w-auto sm:flex-[1_1_180px] sm:min-w-[150px] sm:max-w-[180px]">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All categories</SelectItem>
                  {CATEGORIES.map((cat) => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-full sm:w-auto sm:flex-[1_1_180px] sm:min-w-[130px] sm:max-w-[180px]">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  {EQUIPMENT_SORTS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {/* Import/Export Dropdown Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="hidden sm:flex shrink-0 lg:max-[1199px]:w-11 lg:max-[1199px]:px-0" aria-label="Import / export" title="Import / export">
                    <FileSpreadsheet className="w-4 h-4" />
                    {/* Icon only on narrow desktops, so the row stays on one line */}
                    <span className="lg:max-[1199px]:sr-only">Import / export</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem onClick={() => setIsXeroImporterOpen(true)}>
                    <FileUp className="w-4 h-4" />
                    Xero importer
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => setIsImportDialogOpen(true)}>
                    <Upload className="w-4 h-4" />
                    Import CSV
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleDownloadCSV}>
                    <Download className="w-4 h-4" />
                    Export CSV
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => setIsFullImportDialogOpen(true)}>
                    <Database className="w-4 h-4" />
                    Full import (with history)
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleFullExport} disabled={fullExporting}>
                    {fullExporting ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Database className="w-4 h-4" />
                    )}
                    Full export (with history)
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleDownloadPDF}>
                    <FileDown className="w-4 h-4" />
                    Download PDF
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={handleBulkDeleteHistoricalIncome}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete all historical income
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              
              {/* Bulk Delete Toggle Button */}
              <Button 
                variant={bulkDeleteMode ? "destructive" : "outline"} 
                size="icon"
                onClick={toggleBulkDeleteMode}
                className="hidden sm:flex shrink-0"
                title={bulkDeleteMode ? "Cancel bulk selection" : "Bulk delete items"}
              >
                {bulkDeleteMode ? <RotateCcw className="w-4 h-4" /> : <Trash2 className="w-4 h-4" />}
              </Button>
            </PageToolbar>
            )}
            
            {/* Bulk delete bar: sticks under the search row while you select, so
                Delete selected is always in reach; the table header sits below it */}
            {bulkDeleteMode && (
              <div className="lg:sticky lg:z-20 lg:bg-background lg:pb-3" style={{ top: TOOLBAR_HEIGHT + SECONDARY_TOOLBAR_HEIGHT }}>
              <div className="flex items-center justify-between px-3 h-[60px] bg-brick-soft rounded-cards">
                <div className="flex items-center gap-3">
                  <Checkbox 
                    checked={selectedForDelete.size === filteredEquipment.filter(e => e.is_active !== false).length && selectedForDelete.size > 0}
                    onCheckedChange={selectAllItems}
                  />
                  <span className="text-sm font-medium">
                    {selectedForDelete.size === 0 
                      ? "Select items to delete" 
                      : `${selectedForDelete.size} item${selectedForDelete.size > 1 ? 's' : ''} selected`}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={toggleBulkDeleteMode}
                  >
                    Cancel
                  </Button>
                  <Button 
                    variant="destructive" 
                    size="sm" 
                    onClick={handleBulkDelete}
                    disabled={selectedForDelete.size === 0 || bulkDeleting}
                  >
                    {bulkDeleting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Deleting...
                      </>
                    ) : (
                      <>
                        <Trash2 className="w-4 h-4" />
                        Delete selected
                      </>
                    )}
                  </Button>
                </div>
              </div>
              </div>
            )}

            {/* Equipment Table */}
            {equipmentLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : filteredEquipment.filter(e => e.is_active !== false).length === 0 ? (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-12">
                  {search || categoryFilter !== 'all'
                    ? <Package className="w-12 h-12 text-muted-foreground/50 mb-4" />
                    : <Illustration name="equipment" className="w-full max-w-xs mb-2" />}
                  <h3 className="text-lg font-normal mb-2">{search || categoryFilter !== 'all' ? 'No equipment found' : 'No equipment yet'}</h3>
                  <p className="text-muted-foreground mb-4 text-center max-w-md">
                    {search || categoryFilter !== 'all' ? 'Try adjusting your filters' : 'Add the gear you own and No Half Days will track what each item earns.'}
                  </p>
                  {!(search || categoryFilter !== 'all') && (
                    <Button onClick={openCreateDialog}>
                      <Plus className="w-[18px] h-[18px]" /> Add equipment
                    </Button>
                  )}
                  {/* Onboarding bought: where it's up to, and the way to send the gear list */}
                  {!(search || categoryFilter !== 'all') && onboarding && onboarding.onboarding_stage !== 'done' && (
                    <div className="mt-8 w-full max-w-2xl rounded-cards bg-mist p-4 sm:p-5 space-y-3 text-left" data-testid="equipment-onboarding-progress">
                      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                        <p className="font-medium">We&rsquo;re setting up your gear</p>
                        <Link to={`/help/${onboarding.id}`} className="text-sm text-clay underline underline-offset-4 hover:text-foreground">
                          {onboarding.onboarding_stage === 'booked' ? 'Send us your gear list' : 'Open your onboarding'}
                        </Link>
                      </div>
                      <OnboardingTrack stage={onboarding.onboarding_stage} />
                    </div>
                  )}
                  {/* New accounts with an empty inventory: the onboarding service can import it for them */}
                  {!(search || categoryFilter !== 'all') && subscription && subscription.plan !== 'demo' && !subscription.onboarding_purchased && (
                    <div className="mt-8 w-full max-w-md rounded-cards bg-mist p-4 flex items-start gap-3 text-left" data-testid="equipment-onboarding-offer">
                      <Headset className="w-[18px] h-[18px] mt-0.5 shrink-0 text-muted-foreground" aria-hidden="true" />
                      <div className="min-w-0 space-y-1">
                        <p className="font-medium">Need a hand getting set up?</p>
                        <p className="text-sm text-muted-foreground">
                          Our onboarding service will import your gear list, rates and packages for you, so you can start quoting straight away.
                        </p>
                        <Link to="/settings?tab=billing" className="inline-block text-sm text-clay underline underline-offset-4 hover:text-foreground">
                          See the onboarding service
                        </Link>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ) : (
              <>
              <EquipmentPhoneList
                rows={activeRows.map((row) => ({ ...row, detail: `${formatCurrency(row.item.daily_rate || 0)} / day` }))}
                onOpen={(id) => navigate(`/equipment/${id}`)}
              />
              <Card className={cn(STICKY_TABLE.card, 'max-sm:hidden')}>
                {/* On desktop nothing here clips, so the header can stick under the search row  */}
                <div className="overflow-x-auto lg:overflow-visible">
                  <Table containerClassName={STICKY_TABLE.container} className={EQUIPMENT_TABLE_CLASS}>
                    <TableHeader {...stickyTableHeader(TOOLBAR_HEIGHT + SECONDARY_TOOLBAR_HEIGHT + (bulkDeleteMode ? BULK_BAR_HEIGHT : 0))}>
                      <TableRow>
                        {bulkDeleteMode && (
                          <TableHead className="w-[50px] pl-4">
                            <Checkbox 
                              checked={selectedForDelete.size === filteredEquipment.filter(e => e.is_active !== false).length && selectedForDelete.size > 0}
                              onCheckedChange={selectAllItems}
                            />
                          </TableHead>
                        )}
                        <TableHead className={`hidden sm:table-cell ${bulkDeleteMode ? "" : "pl-4"}`}>Category</TableHead>
                        <TableHead className={`w-full max-w-0 ${bulkDeleteMode ? "" : "sm:pl-0 pl-4"}`}>Name</TableHead>
                        <TableHead className="hidden min-[1200px]:table-cell text-right">Purchase price</TableHead>
                        <TableHead className="text-right">Daily rate</TableHead>
                        <TableHead className="hidden min-[1200px]:table-cell text-right">Rental days</TableHead>
                        <TableHead className="hidden lg:table-cell text-right">Total income</TableHead>
                        <TableHead className="text-right">ROI</TableHead>
                        {!bulkDeleteMode && <TableHead className="w-[50px]"></TableHead>}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {activeRows.map(({ item, roi, roiStatus }) => {
                        const isSelected = selectedForDelete.has(item.id);
                        return (
                          <TableRow 
                            key={item.id}
                            data-testid={`equipment-row-${item.id}`}
                            className={`cursor-pointer hover:bg-mist/50 ${isSelected ? 'bg-brick-soft' : ''}`}
                            onClick={() => bulkDeleteMode ? toggleSelectItem(item.id, { stopPropagation: () => {} }) : navigate(`/equipment/${item.id}`)}
                          >
                            {bulkDeleteMode && (
                              <TableCell className="pl-4">
                                <Checkbox 
                                  checked={isSelected}
                                  onCheckedChange={() => {}}
                                  onClick={(e) => toggleSelectItem(item.id, e)}
                                />
                              </TableCell>
                            )}
                            <TableCell className={`hidden sm:table-cell ${bulkDeleteMode ? "" : "pl-4"}`}>
                              <CategoryBadge category={item.category} />
                            </TableCell>
                            <TableCell className={`w-full max-w-0 ${bulkDeleteMode ? "" : "sm:pl-0 pl-4"}`}>
                              <div className="min-w-0">
                                <p className="font-medium truncate">{item.name}</p>
                                {item.description && (
                                  <p className="text-sm text-muted-foreground truncate">{item.description}</p>
                                )}
                                {/* Mobile: show category badge under name */}
                                <CategoryBadge category={item.category} className="mt-1 sm:hidden" />
                              </div>
                            </TableCell>
                            <TableCell className="hidden min-[1200px]:table-cell text-right tabular-nums">{formatCurrency(item.purchase_price || 0)}</TableCell>
                            <TableCell className="text-right tabular-nums">{formatCurrency(item.daily_rate || 0)}</TableCell>
                            <TableCell className="hidden min-[1200px]:table-cell text-right tabular-nums">{item.total_days || 0}</TableCell>
                            <TableCell className="hidden lg:table-cell text-right tabular-nums">{formatCurrency(item.total_income || 0)}</TableCell>
                            <TableCell className={`text-right tabular-nums ${roiStatus.color}`}>
                              {signedPercent(roi)}
                            </TableCell>
                            {!bulkDeleteMode && (
                              <TableCell>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={(e) => handleDelete(item.id, e)}
                                  data-testid={`archive-equipment-${item.id}`}
                                  title="Archive equipment"
                                >
                                  <Archive className="w-4 h-4 text-muted-foreground hover:text-foreground" />
                                </Button>
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </Card>
              </>
            )}
          </>
        )}

        {/* Kits Tab Content */}
        {activeTab === 'kits' && (
          <>
            {kitsLoading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
              </div>
            ) : equipmentKits.length === 0 ? (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <Illustration name="kits" className="w-full max-w-xs mb-2" />
                  <h3 className="text-lg font-normal mb-2">No kits yet</h3>
                  <p className="text-muted-foreground text-center mb-4">
                    Create your first kit to group equipment items together for faster job setup.
                  </p>
                  <Button onClick={() => { setEditingKitId(null); setKitFormData(INITIAL_KIT_FORM); setIsKitDialogOpen(true); }}>
                    <Plus className="w-[18px] h-[18px]" /> Create your first kit
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <>
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {equipmentKits.map((kit) => {
                    return (
                    <Card 
                      key={kit.id} 
                      className="hover:bg-mist transition-colors cursor-pointer"
                      onClick={() => handleEditKit(kit)}
                    >
                    <CardHeader className="pb-2">
                      <div className="flex justify-between items-start">
                        <div className="flex items-center gap-2">
                          <CardTitle className="text-xl">{kit.name}</CardTitle>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => handleDeleteKit(kit.id, e)}
                          data-testid={`delete-kit-${kit.id}`}
                        >
                          <Trash2 className="w-4 h-4 text-muted-foreground hover:text-destructive" />
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {/* Item count and names - Now at top */}
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Package className="w-4 h-4" />
                        <span>{kit.equipment_items?.length || 0} items</span>
                      </div>
                      <p className="text-sm text-muted-foreground line-clamp-2">
                        {getKitEquipmentNames(kit)}
                      </p>
                      
                      {/* Kit Value Stats - Simplified: only Kit Value and Per Day Value */}
                      <div className="grid grid-cols-2 gap-x-4 gap-y-2 pt-2 border-t border-line">
                        <div>
                          <p className="text-xs text-muted-foreground">Kit value</p>
                          <p className="tabular-nums">{formatCurrency(kit.total_purchase_price || 0)}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-muted-foreground">Per day value</p>
                          <p className="tabular-nums">{formatCurrency(kit.total_daily_rate || 0)}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  );
                })}
                  
                  {/* Ghost placeholder cards - fill the row, hidden on mobile */}
                  {/* First ghost: show on md+ if row not full (md: 2 cols, lg: 3 cols) */}
                  {(equipmentKits.length % 2 !== 0 || equipmentKits.length % 3 !== 0) && (
                    <Card 
                      className="hidden md:flex border border-dashed border-line-strong bg-transparent hover:bg-mist transition-colors cursor-pointer items-center justify-center min-h-[180px]"
                      onClick={() => { setEditingKitId(null); setKitFormData(INITIAL_KIT_FORM); setIsKitDialogOpen(true); }}
                    >
                      <div className="flex flex-col items-center gap-2 text-muted-foreground">
                        <Plus className="w-8 h-8" />
                        <span className="text-sm">Create kit</span>
                      </div>
                    </Card>
                  )}
                  
                  {/* Second ghost: show on lg only if 2 empty slots (kit count % 3 === 1) */}
                  {equipmentKits.length % 3 === 1 && (
                    <Card 
                      className="hidden lg:flex border border-dashed border-line-strong bg-transparent hover:bg-mist transition-colors cursor-pointer items-center justify-center min-h-[180px]"
                      onClick={() => { setEditingKitId(null); setKitFormData(INITIAL_KIT_FORM); setIsKitDialogOpen(true); }}
                    >
                      <div className="flex flex-col items-center gap-2 text-muted-foreground">
                        <Plus className="w-8 h-8" />
                        <span className="text-sm">Create kit</span>
                      </div>
                    </Card>
                  )}
                </div>
              
                {/* ROI Projection Card - Below Kit panes */}
                <ROIProjectionCard 
                  selectedKitId={selectedProjectionKitId}
                  onKitSelect={setSelectedProjectionKitId}
                />
              </>
            )}
          </>
        )}

        {/* Archived Tab Content */}
        {activeTab === 'archived' && (
          <>
            {/* Filters. A phone has the search in its PhonePanel */}
            <div className="max-sm:hidden flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-[18px] h-[18px] text-muted-foreground pointer-events-none" />
                <Input
                  placeholder="Search archived equipment"
                  aria-label="Search archived equipment"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-11"
                />
              </div>
            </div>

            {equipmentLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : filteredEquipment.filter(e => e.is_active === false).length === 0 ? (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-12">
                  {search || categoryFilter !== 'all'
                    ? <Archive className="w-12 h-12 text-muted-foreground/50 mb-4" />
                    : <Illustration name="archive" className="w-full max-w-xs mb-2" />}
                  <h3 className="text-lg font-normal mb-2">No archived equipment</h3>
                  <p className="text-muted-foreground">
                    Archived equipment will appear here. You can archive items when they are no longer in use.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <>
              <EquipmentPhoneList
                rows={archivedRows.map((row) => ({ ...row, detail: `Sold for ${formatCurrency(row.item.sale_price || 0)}` }))}
                onOpen={(id) => navigate(`/equipment/${id}`)}
                muted
              />
              <Card className={cn(STICKY_TABLE.card, 'max-sm:hidden')}>
                {/* On desktop nothing here clips, so the header can stick under the search row  */}
                <div className="overflow-x-auto lg:overflow-visible">
                  <Table containerClassName={STICKY_TABLE.container} className={EQUIPMENT_TABLE_CLASS}>
                    <TableHeader {...stickyTableHeader(TOOLBAR_HEIGHT + SECONDARY_TOOLBAR_HEIGHT)}>
                      <TableRow>
                        <TableHead className="pl-4">Category</TableHead>
                        <TableHead className="w-full max-w-0">Name</TableHead>
                        <TableHead className="hidden min-[1200px]:table-cell text-right">Purchase price</TableHead>
                        <TableHead className="text-right">Total income</TableHead>
                        <TableHead className="text-right">Sale price</TableHead>
                        <TableHead className="text-right">ROI</TableHead>
                        <TableHead className="w-[120px]"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {archivedRows.map(({ item, roi, roiStatus }) => {
                        const salePrice = item.sale_price || 0;
                        return (
                          <TableRow 
                            key={item.id}
                            data-testid={`equipment-row-${item.id}`}
                            className="cursor-pointer hover:bg-mist/50 opacity-75"
                            onClick={() => navigate(`/equipment/${item.id}`)}
                          >
                            <TableCell className="pl-4">
                              <CategoryBadge category={item.category} />
                            </TableCell>
                            <TableCell className="w-full max-w-0">
                              <div className="flex items-center gap-2 min-w-0">
                                <p className="font-medium truncate">{item.name}</p>
                                <Badge variant="outline" className="text-xs">Archived</Badge>
                              </div>
                            </TableCell>
                            <TableCell className="hidden min-[1200px]:table-cell text-right tabular-nums">{formatCurrency(item.purchase_price || 0)}</TableCell>
                            <TableCell className="text-right tabular-nums">{formatCurrency(item.total_income || 0)}</TableCell>
                            <TableCell className="text-right tabular-nums">{formatCurrency(salePrice)}</TableCell>
                            <TableCell className={`text-right tabular-nums ${roiStatus.color}`}>
                              {signedPercent(roi)}
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-1">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={(e) => handleRestore(item.id, e)}
                                  disabled={restoring === item.id || deleting === item.id}
                                  data-testid={`restore-equipment-${item.id}`}
                                  title="Restore equipment"
                                >
                                  {restoring === item.id ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <RotateCcw className="w-4 h-4 text-muted-foreground hover:text-primary" />
                                  )}
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={(e) => handlePermanentDelete(item.id, item.name, e)}
                                  disabled={restoring === item.id || deleting === item.id}
                                  data-testid={`delete-equipment-${item.id}`}
                                  title="Permanently delete"
                                >
                                  {deleting === item.id ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <Trash2 className="w-4 h-4 text-muted-foreground hover:text-destructive" />
                                  )}
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </Card>
              </>
            )}
          </>
        )}

        {/* Kit Dialog */}
        <Dialog open={isKitDialogOpen} onOpenChange={setIsKitDialogOpen}>
          <DialogContent className="w-[calc(100vw-2rem)] max-w-lg max-h-[85vh] sm:max-h-[80vh] overflow-hidden flex flex-col">
            <DialogHeader className="flex-shrink-0">
              <DialogTitle>{editingKitId ? 'Edit kit' : 'Create kit'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleKitSubmit} className="flex flex-col flex-1 overflow-hidden space-y-4">
              <div className="space-y-2 flex-shrink-0">
                <Label htmlFor="kit-name">Kit name</Label>
                <Input
                  id="kit-name"
                  value={kitFormData.name}
                  onChange={(e) => setKitFormData({ ...kitFormData, name: e.target.value })}
                  placeholder="e.g., Standard Camera Package"
                  required
                  data-testid="kit-name-input"
                />
              </div>
              
              <div className="space-y-2 flex-1 flex flex-col overflow-hidden">
                <Label className="flex-shrink-0">Equipment items</Label>
                <p className="text-sm text-muted-foreground flex-shrink-0">Select equipment to include in this kit</p>
                
                {equipment.length === 0 ? (
                  <div className="text-center py-4 text-muted-foreground">
                    No equipment available. Add equipment first.
                  </div>
                ) : (
                  <div className="border border-line rounded-cards flex-1 overflow-y-auto min-h-0">
                    {equipment.map((equip) => {
                      const isSelected = kitFormData.equipment_items.some(
                        item => item.equipment_id === equip.id
                      );
                      
                      return (
                        <div
                          key={equip.id}
                          className={`flex items-center gap-2 sm:gap-3 p-2 sm:p-3 border-b border-line last:border-b-0 hover:bg-mist/50 cursor-pointer ${
                            isSelected ? 'bg-mist' : ''
                          } ${equip.archived ? 'opacity-60' : ''}`}
                          onClick={() => toggleEquipmentInKit(equip.id)}
                          data-testid={`kit-equip-row-${equip.id}`}
                        >
                          <div 
                            className={`w-5 h-5 flex-shrink-0 rounded-[4px] border flex items-center justify-center ${
                              isSelected 
                                ? 'bg-primary border-primary' 
                                : 'border-line-strong'
                            }`}
                          >
                            {isSelected && (
                              <svg className="w-3 h-3 text-primary-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </div>
                          <div className="flex-1 min-w-0 overflow-hidden">
                            <div className="font-medium truncate text-sm sm:text-base flex items-center gap-2" title={equip.name}>
                              {equip.name}
                              {equip.archived && <Badge variant="secondary" className="text-xs">Archived</Badge>}
                            </div>
                            <div className="text-xs sm:text-sm text-muted-foreground truncate">
                              {equip.category} • {formatCurrency(equip.daily_rate)}/day
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {kitFormData.equipment_items.length > 0 && (
                <div className="bg-mist rounded-cards p-3 flex-shrink-0">
                  <div className="text-sm font-medium">
                    {kitFormData.equipment_items.length} item(s) selected
                  </div>
                </div>
              )}

              <Button type="submit" className="w-full flex-shrink-0" data-testid="save-kit-btn">
                {editingKitId ? 'Save changes' : 'Create kit'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Import CSV Dialog */}
        <Dialog open={isImportDialogOpen} onOpenChange={(open) => {
          setIsImportDialogOpen(open);
          if (!open) {
            setImportFile(null);
            setImportPreview([]);
          }
        }}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Import equipment from CSV</DialogTitle>
              <DialogDescription>
                Upload a CSV file to bulk import equipment items.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              {/* Download Template */}
              <div className="flex items-center justify-between p-3 bg-mist rounded-cards">
                <div>
                  <p className="text-sm">CSV template</p>
                  <p className="text-xs text-muted-foreground">Download template with example data</p>
                </div>
                <Button variant="outline" size="sm" onClick={handleDownloadTemplate}>
                  <Download className="w-4 h-4" /> Download
                </Button>
              </div>
              
              {/* Format info */}
              <div className="text-xs text-muted-foreground space-y-1 p-3 bg-mist/50 rounded-cards">
                <p><strong>Required columns:</strong> name, category, daily_rate</p>
                <p><strong>Optional:</strong> description, purchase_price, purchase_date</p>
                <p><strong>Categories:</strong> Camera, Lenses, Lighting, Grip, Audio, Monitors, Accessories, Other</p>
              </div>
              
              {/* File Upload */}
              <FileDropzone accept=".csv" onSelect={handleFileSelect} title="Choose a CSV file" />
              
              {/* Preview */}
              {importPreview.length > 0 && (
                <div className="space-y-2">
                  <Label>Preview (first {importPreview.length} items)</Label>
                  <div className="max-h-[200px] overflow-auto border border-line rounded-cards">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-xs">Name</TableHead>
                          <TableHead className="text-xs">Category</TableHead>
                          <TableHead className="text-xs text-right">Daily rate</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {importPreview.map((item, idx) => (
                          <TableRow key={idx}>
                            <TableCell className="text-sm py-2">{item.name}</TableCell>
                            <TableCell className="text-sm py-2">
                              <CategoryBadge category={item.category} className="text-xs" />
                            </TableCell>
                            <TableCell className="text-sm py-2 text-right tabular-nums">
                              {formatCurrency(parseFloat(item.daily_rate) || 0)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
              
              {/* Import Button */}
              <Button 
                onClick={handleImportCSV} 
                disabled={!importFile || importing}
                className="w-full"
              >
                {importing ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Importing...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4" />
                    Import equipment
                  </>
                )}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Full Import CSV Dialog (with History) */}
        <Dialog open={isFullImportDialogOpen} onOpenChange={(open) => {
          setIsFullImportDialogOpen(open);
          if (!open) {
            setFullImportFile(null);
            setFullImportPreview([]);
          }
        }}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Full import: equipment with history</DialogTitle>
              <DialogDescription>
                Import equipment items along with their complete rental history.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              {/* Download Template */}
              <div className="flex items-center justify-between p-3 bg-mist rounded-cards">
                <div>
                  <p className="text-sm">Full CSV template</p>
                  <p className="text-xs text-muted-foreground">Includes equipment + history columns</p>
                </div>
                <Button variant="outline" size="sm" onClick={handleDownloadFullTemplate}>
                  <Download className="w-4 h-4" /> Download
                </Button>
              </div>
              
              {/* Format info */}
              <div className="text-xs text-muted-foreground space-y-2 p-3 bg-mist/50 rounded-cards">
                <p><strong>How it works:</strong></p>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>Row with <code className="bg-mist px-1 rounded-[4px]">name</code> starts a new item</li>
                  <li>Subsequent rows with empty equipment columns add history to that item</li>
                  <li>All imported history will be marked as &quot;historical&quot; type</li>
                </ul>
                <p className="mt-2"><strong>Equipment columns:</strong> name, category, description, purchase_price, daily_rate, purchase_date</p>
                <p><strong>History columns:</strong> history_date, history_description, history_client, history_days, history_income</p>
              </div>
              
              {/* File Upload */}
              <FileDropzone accept=".csv" onSelect={handleFullFileSelect} title="Choose a CSV file" />
              
              {/* Preview */}
              {fullImportPreview.equipment && fullImportPreview.equipment.length > 0 && (
                <div className="space-y-2">
                  <Label>Preview ({fullImportPreview.equipment.length} equipment items, {fullImportPreview.historyCount + fullImportPreview.equipment.filter(e => e.historyEntries > 0).length} history entries)</Label>
                  <div className="max-h-[200px] overflow-auto border border-line rounded-cards">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-xs">Name</TableHead>
                          <TableHead className="text-xs">Category</TableHead>
                          <TableHead className="text-xs text-right">Daily rate</TableHead>
                          <TableHead className="text-xs text-right">History entries</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {fullImportPreview.equipment.slice(0, 10).map((item, idx) => (
                          <TableRow key={idx}>
                            <TableCell className="text-sm py-2">{item.name}</TableCell>
                            <TableCell className="text-sm py-2">
                              <CategoryBadge category={item.category} className="text-xs" />
                            </TableCell>
                            <TableCell className="text-sm py-2 text-right tabular-nums">
                              {formatCurrency(parseFloat(item.daily_rate) || 0)}
                            </TableCell>
                            <TableCell className="text-sm py-2 text-right">
                              {item.historyEntries > 0 ? (
                                <Badge variant="secondary">{item.historyEntries}</Badge>
                              ) : (
                                <span className="text-muted-foreground">-</span>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                        {fullImportPreview.equipment.length > 10 && (
                          <TableRow>
                            <TableCell colSpan={4} className="text-center text-sm text-muted-foreground py-2">
                              ... and {fullImportPreview.equipment.length - 10} more items
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
              
              {/* Import Button */}
              <Button 
                onClick={handleFullImportCSV} 
                disabled={!fullImportFile || fullImporting}
                className="w-full"
              >
                {fullImporting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Importing...
                  </>
                ) : (
                  <>
                    <Database className="w-4 h-4" />
                    Import equipment with history
                  </>
                )}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        
        {/* Disposal Dialog */}
        <Dialog open={showDisposalDialog} onOpenChange={handleDisposalCancel}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Equipment disposal</DialogTitle>
              <DialogDescription>
                Record the sale details for this equipment. This will archive the item and track the recovered value separately from rental income.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="sale_price">Sale price ({regionConfig.currency.symbol})</Label>
                <Input
                  id="sale_price"
                  type="number"
                  step="0.01"
                  value={disposalForm.sale_price}
                  onChange={(e) => setDisposalForm({ ...disposalForm, sale_price: e.target.value })}
                  placeholder="0.00"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="disposal_date">Disposal date</Label>
                <DatePicker
                  value={disposalForm.disposal_date}
                  onChange={(value) => setDisposalForm({ ...disposalForm, disposal_date: value })}
                  placeholder="Select date"
                  region={regionConfig.code}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="buyer_notes">Buyer / Notes</Label>
                <Textarea
                  id="buyer_notes"
                  value={disposalForm.buyer_notes}
                  onChange={(e) => setDisposalForm({ ...disposalForm, buyer_notes: e.target.value })}
                  placeholder="e.g., Sold to John Smith, buyer contact info, etc."
                  className="h-20"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={handleDisposalCancel}>Cancel</Button>
              <Button onClick={handleDisposalSubmit}>Archive & record sale</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        {/* Xero Importer */}
        <XeroImporter
          isOpen={isXeroImporterOpen}
          onClose={() => setIsXeroImporterOpen(false)}
          onImportComplete={() => {
            fetchEquipment();
            setIsXeroImporterOpen(false);
          }}
          equipment={activeEquipment}
        />
      </div>
    </Layout>
  );
}
